import {useMemo} from 'react'
import {HttpGetPlannedAccessesDetailResponse} from '@/features/planned-accesses/http/plannedAccesses.http'
import {
    calculateTotalPlannedAccesses,
    calculateTotalAccessInAbsence,
    calculateTotalAccesses
} from '@/features/projects/utils'

/**
 * Custom hook to calculate various metrics related to planned accesses
 * @param plannedAccesses The planned accesses data from the API
 * @returns Calculated metrics for planned accesses
 */
export const usePlannedAccessesCalculations = (
    duration: number,
    plannedAccesses: HttpGetPlannedAccessesDetailResponse | undefined
) => {
    // Calculate total accesses across all types
    const totalPlannedAccesses = useMemo(() => {
        return plannedAccesses ? calculateTotalPlannedAccesses(duration, plannedAccesses) : 0
    }, [plannedAccesses])

    // Calculate total accesses in absence (non in-person)
    const totalPlannedAccessesInAbsence = useMemo(() => {
        return plannedAccesses ? calculateTotalAccessInAbsence(plannedAccesses) : 0
    }, [plannedAccesses])

    // Calculate total accesses in presence (in-person)
    const totalPlannedAccessesInPresence = useMemo(() => {
        return totalPlannedAccesses - totalPlannedAccessesInAbsence
    }, [totalPlannedAccesses, totalPlannedAccessesInAbsence])

    // Calculate total treatments (all frequencies combined)
    const totalPlannedTreatments = useMemo(() => {
        return plannedAccesses
            ? plannedAccesses.treatments.reduce(
                  (sum, item) =>
                      sum + calculateTotalAccesses(duration, item.frequency ?? 'weekly', item.numberOfAccesses),
                  0
              )
            : 0
    }, [plannedAccesses])

    // Calculate total single accesses
    const totalSingleAccesses = useMemo(() => {
        return plannedAccesses
            ? plannedAccesses.in_person_accesses.reduce((sum, item) => sum + item.numberOfAccesses, 0)
            : 0
    }, [plannedAccesses])

    // Filter treatments by frequency type
    const treatmentsOnWeek = useMemo(() => {
        return plannedAccesses?.treatments.filter(treatment => treatment.frequency === 'weekly')
    }, [plannedAccesses])

    const treatmentsOnMonth = useMemo(() => {
        return plannedAccesses?.treatments.filter(treatment => treatment.frequency === 'monthly')
    }, [plannedAccesses])

    // Calculate total treatments by frequency
    const totalPlannedTreatmentsForWeek = useMemo(() => {
        return treatmentsOnWeek ? treatmentsOnWeek.reduce((sum, item) => sum + item.numberOfAccesses, 0) : 0
    }, [treatmentsOnWeek])

    const totalPlannedTreatmentsForMonth = useMemo(() => {
        return treatmentsOnMonth ? treatmentsOnMonth.reduce((sum, item) => sum + item.numberOfAccesses, 0) : 0
    }, [treatmentsOnMonth])

    return {
        totalPlannedAccesses,
        totalPlannedAccessesInAbsence,
        totalPlannedAccessesInPresence,
        totalPlannedTreatments,
        totalSingleAccesses,
        treatmentsOnWeek,
        treatmentsOnMonth,
        totalPlannedTreatmentsForWeek,
        totalPlannedTreatmentsForMonth
    }
}
